'use client';

import { useFormTemplateKey } from '@commenda/api/form-builder/form-template-keys';
import { useFormsTemplate } from '@commenda/api/form-builder/forms-templates';
import {
  Badge,
  Input,
  Label,
  LoadingIndicator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import { Info } from 'lucide-react';
import { useMemo } from 'react';
import { getFieldsFromTemplateDSL } from '../../utils/dsl-traversal';
import { previewEntryTitleTemplate } from '../../utils/entry-title';
import { EntryTitleTemplateField } from './EntryTitleTemplateField';
import { KeySelector } from './KeySelector';

export function KeyPersonsEditor({
  props,
  onUpdateProps,
}: {
  props: Record<string, unknown>;
  onUpdateProps: (key: string, value: unknown) => void;
}) {
  const mode = (props.mode as 'select-add' | 'view-edit') ?? 'select-add';
  const isViewEditMode = mode === 'view-edit';
  const keyId = (props.keyId as string) || '';
  const { key } = useFormTemplateKey(keyId || null);
  const { template, isLoading: isLoadingTemplate } = useFormsTemplate(
    key?.liveFormId ?? null
  );

  const linkedFields = useMemo(() => {
    if (!template?.dsl) return [];
    return getFieldsFromTemplateDSL(template.dsl);
  }, [template?.dsl]);

  const linkedFormEntryTitleTemplate =
    template?.dsl?.form?.settings?.display?.entryTitleTemplate;

  return (
    <div className="space-y-4">
      <h3 className="txt-small-medium text-text-secondary">Key Persons</h3>

      <div className="space-y-2">
        <Label>Mode</Label>
        <Select
          value={mode}
          onValueChange={(v) =>
            onUpdateProps('mode', v as 'select-add' | 'view-edit')
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Select mode" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="select-add">
              Select & add (incorporation)
            </SelectItem>
            <SelectItem value="view-edit">View & edit (onboarding)</SelectItem>
          </SelectContent>
        </Select>
        <p className="txt-xs-regular text-text-tertiary">
          {isViewEditMode
            ? 'Lists all directors linked to the corporation; edit only, no add/remove.'
            : 'Let users select existing key persons or add new ones, then link to the entity.'}
        </p>
      </div>

      <div className="space-y-2">
        <Label>Per-person form</Label>
        <KeySelector
          value={keyId}
          onValueChange={(v) => onUpdateProps('keyId', v)}
          placeholder="Select a form template"
        />
        <p className="txt-xs-regular text-text-tertiary">
          The form that opens for each key person to collect their details
          (personal info, documents, etc.)
        </p>
      </div>

      {keyId && (
        <div className="space-y-2">
          {isLoadingTemplate ? (
            <div className="flex items-center gap-2 py-1">
              <LoadingIndicator />
              <span className="txt-xs-regular text-text-tertiary">
                Loading fields...
              </span>
            </div>
          ) : linkedFields.length > 0 ? (
            <div className="p-3 bg-bg-secondary rounded-md border border-border-secondary">
              <p className="txt-xs-regular text-text-tertiary mb-2">
                <Info className="inline-block w-3 h-3 mr-1" />
                Linked form collects {linkedFields.length} field
                {linkedFields.length !== 1 ? 's' : ''}:
              </p>
              <div className="flex flex-wrap gap-1">
                {linkedFields.map((f) => (
                  <Badge key={f.fieldId} variant="gray" className="text-xs">
                    {f.label}
                  </Badge>
                ))}
              </div>
            </div>
          ) : (
            <p className="txt-xs-regular text-text-tertiary">
              No fields found in the linked form
            </p>
          )}
        </div>
      )}

      {keyId && (
        <EntryTitleTemplateField
          label="Card title override"
          description={
            linkedFormEntryTitleTemplate
              ? `Leave empty to use the linked form's own setting: ${previewEntryTitleTemplate(linkedFormEntryTitleTemplate, linkedFields)}`
              : "The linked form has no entry title configured — set one here, or in that form's settings."
          }
          value={(props.entryTitleTemplate as string) || ''}
          fields={linkedFields}
          onChange={(value) =>
            onUpdateProps('entryTitleTemplate', value || undefined)
          }
        />
      )}

      {!isViewEditMode && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Min selection</Label>
              <Input
                type="number"
                min={0}
                value={
                  props.minSelection != null ? String(props.minSelection) : '1'
                }
                onChange={(e) => {
                  const v = e.target.value;
                  onUpdateProps(
                    'minSelection',
                    v === '' ? undefined : Number(v)
                  );
                }}
                placeholder="1"
              />
            </div>
            <div className="space-y-2">
              <Label>Max selection</Label>
              <Input
                type="number"
                min={0}
                value={
                  props.maxSelection != null ? String(props.maxSelection) : ''
                }
                onChange={(e) => {
                  const v = e.target.value;
                  onUpdateProps(
                    'maxSelection',
                    v === '' ? undefined : Number(v)
                  );
                }}
                placeholder="No limit"
              />
              <p className="txt-xs-regular text-text-tertiary">0 = unlimited</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Add new label</Label>
            <Input
              value={(props.addNewLabel as string) || ''}
              onChange={(e) => onUpdateProps('addNewLabel', e.target.value)}
              placeholder="Add new key person"
            />
          </div>
        </>
      )}
    </div>
  );
}
